import { useState, useCallback, useRef } from 'react'
import api from '../services/api'

export const DEFAULT_CONTEXT_LINES = 100
export const DEFAULT_CONTEXT_LINES_PRO = Infinity
const MAX_FLUID_ITERATIONS_NORMAL = 6
const MAX_FLUID_ITERATIONS_PRO = 12

// ─── Helpers de detecção ────────────────────────────────────────────────────

/** Perfis Linux (os que têm sudo). RouterOS não tem. */
export function isLinuxProfileSlug(slug) {
  return slug !== 'MikrotikRouterOS'
}

/** Detecta "permission denied" no output do terminal. */
function isPermissionError(output) {
  return /permission denied|operation not permitted|must be (run )?as root|requires? root|not permitted|cannot open.*: permission/i.test(output)
}

/** Detecta prompt de senha do sudo no output. */
function isPasswordPrompt(output) {
  return /\[sudo\] password|password for .+:|sudo:.*password is required/i.test(output)
}

/**
 * Padrões de comandos que tipicamente precisam de sudo em Linux.
 * Usado para indicação visual proativa no CommandCard.
 */
const SUDO_LIKELY_PATTERNS = [
  /^\s*systemctl\s+(start|stop|restart|enable|disable|mask|unmask)\b/i,
  /^\s*apt(-get)?\s+(install|remove|purge|update|upgrade|autoremove)\b/i,
  /^\s*dnf\s+(install|remove|update|erase|upgrade)\b/i,
  /^\s*yum\s+(install|remove|update|erase)\b/i,
  /^\s*iptables\b/i,
  /^\s*ip6tables\b/i,
  /^\s*ufw\b/i,
  /^\s*firewall-cmd\b/i,
  /^\s*nft\s+(flush|add|delete)\b/i,
  /^\s*(adduser|useradd|userdel|usermod|groupadd|groupdel)\b/i,
  /^\s*passwd\b/i,
  /^\s*(mount|umount)\b/i,
  /^\s*(fdisk|parted|mkfs)\b/i,
  /^\s*(modprobe|rmmod)\b/i,
  />\s*\/etc\//,
  />\s*\/usr\//,
  />\s*\/boot\//,
  /\btee\s+\/etc\//,
  /^\s*(vim?|nano|vi)\s+\/etc\//i,
  /^\s*setenforce\b/i,
  /^\s*sysctl\s+-w\b/i,
]

export function commandLikelyNeedsSudo(command) {
  return SUDO_LIKELY_PATTERNS.some((p) => p.test(command))
}

/**
 * Sanitiza comandos para execução não-interativa.
 * Previne pagers (less/more) que travam o terminal aguardando input.
 */
function sanitizeForNonInteractive(cmd) {
  if (!cmd) return cmd
  let s = cmd
  if (/\bsystemctl\b/.test(s) && !s.includes('--no-pager'))
    s = s.replace(/\bsystemctl\b/, 'systemctl --no-pager')
  if (/\bjournalctl\b/.test(s) && !s.includes('--no-pager'))
    s = s.replace(/\bjournalctl\b/, 'journalctl --no-pager')
  if (/\bgit\s+(log|diff|show|blame)\b/.test(s) && !s.includes('--no-pager'))
    s = s.replace(/\b(git)\s+(log|diff|show|blame)\b/, '$1 --no-pager $2')
  s = s.replace(/\|\s*(less|more)(\s|$)/g, '$2')
  return s.trim()
}

/** Escapa aspas simples para uso seguro em shell: echo 'PASS' | sudo -S */
function escapeForShell(str) {
  return str.replace(/'/g, "'\\''")
}

/**
 * Monta o comando sudo de acordo com o profile da plataforma.
 * RouterOS não tem sudo — retorna o comando original.
 */
function buildSudoCommand(command, profileSlug) {
  if (!isLinuxProfileSlug(profileSlug)) return command
  return `sudo ${command}`
}

function hasAutoRunnableCommands(data) {
  return (data?.suggestedCommands || []).some((c) => !c.requiresConfirmation)
}

/**
 * Determina se deve exibir o planner antes de executar.
 * Normal: mostra quando há mais de 3 comandos automáticos.
 * Pro:    mostra quando há mais de 1 comando automático.
 */
function shouldShowExecutionPlan(data, settings) {
  if (!settings.autoApproveReads) return false
  const autoCount = (data?.suggestedCommands || []).filter((c) => !c.requiresConfirmation).length
  if (autoCount === 0) return false
  const threshold = settings.proMode ? 1 : 3
  return autoCount > threshold
}

function proSessionKey(deviceId) {
  return `automais_ai_pro_conv_${deviceId}`
}

// ────────────────────────────────────────────────────────────────────────────

export function useTerminalAI({
  systemProfileSlug,
  deviceId,
  deviceName,
  getTerminalBuffer,
  executeCommand,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [settings, setSettings] = useState({
    autoApproveReads: false,
    autoApproveWrites: false,
    proMode: false,
    liteMode: false,
  })
  const [tokenStats, setTokenStats] = useState({ input: 0, output: 0 })

  // ─── Su modal ─────────────────────────────────────────────────────────────
  const [suModal, setSuModal] = useState(null) // { mode, command }
  const suModalCallbackRef = useRef(null)       // { resolve, reject }

  // ─── Execution Plan (planner) ─────────────────────────────────────────────
  const [executionPlan, setExecutionPlan] = useState(null) // { message, commands }
  const executionPlanCallbackRef = useRef(null)             // { resolve, reject }

  const showExecutionPlan = useCallback((data) => {
    return new Promise((resolve, reject) => {
      executionPlanCallbackRef.current = { resolve, reject }
      setExecutionPlan({
        message: data.message,
        commands: (data.suggestedCommands || []).filter((c) => !c.requiresConfirmation),
      })
    })
  }, [])

  const approveExecutionPlan = useCallback(() => {
    setExecutionPlan(null)
    executionPlanCallbackRef.current?.resolve(true)
  }, [])

  const rejectExecutionPlan = useCallback(() => {
    setExecutionPlan(null)
    executionPlanCallbackRef.current?.reject(new Error('cancelled'))
  }, [])

  /** Abre o modal e retorna uma Promise que resolve quando o usuário agir. */
  const openSuModal = useCallback((mode, command) => {
    return new Promise((resolve, reject) => {
      suModalCallbackRef.current = { resolve, reject }
      setSuModal({ mode, command })
    })
  }, [])

  const confirmSu = useCallback((value) => {
    setSuModal(null)
    suModalCallbackRef.current?.resolve(value)
  }, [])

  const cancelSu = useCallback(() => {
    setSuModal(null)
    suModalCallbackRef.current?.reject(new Error('cancelled'))
  }, [])

  // ─── Execução com detecção automática de escalada ─────────────────────────
  const executeWithSuCheck = useCallback(
    async (rawCommand) => {
      if (!executeCommand) return '(sem executor de comandos)'

      const command = sanitizeForNonInteractive(rawCommand)
      const output = await executeCommand(command)

      if (!isLinuxProfileSlug(systemProfileSlug)) return output
      if (!isPermissionError(output)) return output

      try {
        await openSuModal('elevation', rawCommand)
      } catch {
        return output
      }

      const sudoCmd = buildSudoCommand(command, systemProfileSlug)
      const sudoOutput = await executeCommand(`${sudoCmd} 2>&1`)

      if (!isPasswordPrompt(sudoOutput) && !isPermissionError(sudoOutput)) {
        return sudoOutput
      }

      let password
      try {
        password = await openSuModal('password', rawCommand)
      } catch {
        return sudoOutput
      }

      const escaped = escapeForShell(password)
      const withPassword = `echo '${escaped}' | sudo -S ${command} 2>&1`
      return await executeCommand(withPassword)
    },
    [executeCommand, systemProfileSlug, openSuModal]
  )

  // ─── Conversa ─────────────────────────────────────────────────────────────
  const conversationIdRef = useRef(null)
  const fluidRunningRef = useRef(false)
  const settingsRef = useRef(settings)
  settingsRef.current = settings

  /** Guarda a última mensagem que falhou por sobrecarga — permite reenviar no modo Lite. */
  const lastFailedRef = useRef(null)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((v) => !v), [])

  const newConversation = useCallback(() => {
    conversationIdRef.current = null
    if (deviceId) sessionStorage.removeItem(proSessionKey(deviceId))
    setMessages([])
    setError(null)
    setTokenStats({ input: 0, output: 0 })
  }, [deviceId])

  const loadProConversationId = useCallback(() => {
    if (!deviceId) return null
    return sessionStorage.getItem(proSessionKey(deviceId)) || null
  }, [deviceId])

  const saveProConversationId = useCallback((id) => {
    if (!deviceId || !id) return
    sessionStorage.setItem(proSessionKey(deviceId), id)
  }, [deviceId])

  const postChat = useCallback(
    async ({ userMessage, terminalContext }) => {
      const s = settingsRef.current

      if (s.proMode && !conversationIdRef.current) {
        conversationIdRef.current = loadProConversationId()
      }

      const { data } = await api.post('/ai/terminal-chat', {
        systemProfileSlug,
        deviceId,
        deviceName,
        conversationId: conversationIdRef.current,
        terminalContext: terminalContext ?? null,
        userMessage,
        proMode: s.proMode,
        liteMode: s.liteMode,
        settings: {
          autoApproveReads: s.autoApproveReads,
          autoApproveWrites: s.autoApproveWrites,
        },
      })

      conversationIdRef.current = data.conversationId

      if (s.proMode) {
        saveProConversationId(data.conversationId)
      }

      if (data.inputTokens || data.outputTokens) {
        setTokenStats((prev) => ({
          input: prev.input + (data.inputTokens || 0),
          output: prev.output + (data.outputTokens || 0),
        }))
      }

      return data
    },
    [systemProfileSlug, deviceId, deviceName, loadProConversationId, saveProConversationId]
  )

  // ─── Loop fluido ──────────────────────────────────────────────────────────
  const runFluidAutoLoop = useCallback(
    async (initialData) => {
      if (!executeWithSuCheck || fluidRunningRef.current) return
      fluidRunningRef.current = true
      setLoading(true)

      const maxIterations = settingsRef.current.proMode
        ? MAX_FLUID_ITERATIONS_PRO
        : MAX_FLUID_ITERATIONS_NORMAL

      let cur = initialData
      let iteration = 0

      try {
        while (iteration < maxIterations) {
          iteration++

          if (cur.offTopic) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: cur.message, commands: [], offTopic: true, timestamp: Date.now() },
            ])
            return
          }

          const cmds = cur.suggestedCommands || []
          const auto = cmds.filter((c) => !c.requiresConfirmation)
          const manual = cmds.filter((c) => c.requiresConfirmation)

          if (auto.length === 0) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: cur.message, commands: manual, timestamp: Date.now() },
            ])
            return
          }

          if (cur.message?.trim()) {
            setMessages((prev) => [
              ...prev,
              { role: 'thinking', content: cur.message, timestamp: Date.now() },
            ])
          }

          let batch = ''
          for (const c of auto) {
            try {
              const out = await executeWithSuCheck(c.command)
              batch += `$ ${c.command}\n${out || '(sem output)'}\n\n`
            } catch (err) {
              batch += `$ ${c.command}\nErro: ${err?.message || 'falha'}\n\n`
            }
          }

          const loopInstruction = settingsRef.current.proMode
            ? 'Resultado dos comandos executados (abaixo). '
              + 'Analise completamente o output. Se a causa raiz do problema original foi identificada, conclua com diagnóstico + solução em "message" e use "commands": []. '
              + 'Se o output NÃO resolve o problema ou revela novas pistas, continue investigando: sugira os próximos comandos necessários.'
            : 'Resultado dos comandos executados (abaixo). '
              + 'Se a pergunta original do usuário JÁ PODE SER RESPONDIDA com esses dados, conclua AGORA: coloque a resposta em "message" e use "commands": []. '
              + 'Só sugira mais comandos se for ESTRITAMENTE necessário para responder o que foi perguntado.'

          cur = await postChat({
            userMessage: loopInstruction,
            terminalContext: batch.trim(),
          })

          if (manual.length > 0) {
            setMessages((prev) => [
              ...prev,
              { role: 'assistant', content: cur.message, commands: manual, timestamp: Date.now() },
            ])
            return
          }
        }

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `${cur.message}\n\n*(Limite de passos automáticos atingido — continue manualmente se precisar.)*`,
            commands: cur.suggestedCommands || [],
            timestamp: Date.now(),
          },
        ])
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Erro no agente AI.'
        setError(msg)
        setMessages((prev) => [
          ...prev,
          { role: 'error', content: msg, timestamp: Date.now() },
        ])
      } finally {
        fluidRunningRef.current = false
        setLoading(false)
      }
    },
    [executeWithSuCheck, postChat]
  )

  // ─── sendMessage ──────────────────────────────────────────────────────────
  const sendMessageRef = useRef(null)

  const sendMessage = useCallback(
    async (userMessage, extraContext, options = {}) => {
      if (!userMessage?.trim()) return

      const { uiContent } = options
      const terminalContext =
        extraContext !== undefined
          ? extraContext
          : getTerminalBuffer
            ? getTerminalBuffer()
            : null

      const contentForChat = uiContent != null ? uiContent : userMessage

      setMessages((prev) => [
        ...prev,
        { role: 'user', content: contentForChat, timestamp: Date.now() },
      ])
      setLoading(true)
      setError(null)

      try {
        const data = await postChat({ userMessage, terminalContext })

        if (data.suggestLiteAgent) {
          lastFailedRef.current = { userMessage, terminalContext }
          setMessages((prev) => [
            ...prev,
            { role: 'error', content: data.message, suggestLiteAgent: true, timestamp: Date.now() },
          ])
          return
        }

        const fluid =
          settingsRef.current.autoApproveReads &&
          hasAutoRunnableCommands(data) &&
          !data.offTopic

        if (fluid) {
          setLoading(false)

          // Mostra o planner se o número de comandos automáticos exceder o threshold
          if (shouldShowExecutionPlan(data, settingsRef.current)) {
            try {
              await showExecutionPlan(data)
            } catch {
              // Usuário cancelou o plano → exibe comandos como aprovação manual
              setMessages((prev) => [
                ...prev,
                {
                  role: 'assistant',
                  content: data.message,
                  commands: data.suggestedCommands || [],
                  offTopic: false,
                  timestamp: Date.now(),
                },
              ])
              return
            }
          }

          await runFluidAutoLoop(data)
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: data.message,
              commands: data.suggestedCommands || [],
              offTopic: data.offTopic || false,
              timestamp: Date.now(),
            },
          ])
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || 'Erro ao contactar o agente AI.'
        setError(msg)
        setMessages((prev) => [
          ...prev,
          { role: 'error', content: msg, timestamp: Date.now() },
        ])
      } finally {
        setLoading(false)
      }
    },
    [getTerminalBuffer, postChat, runFluidAutoLoop]
  )

  sendMessageRef.current = sendMessage

  // ─── retryAsLite ──────────────────────────────────────────────────────────
  const retryAsLite = useCallback(() => {
    if (!lastFailedRef.current) return
    const { userMessage, terminalContext } = lastFailedRef.current
    lastFailedRef.current = null
    setSettings((prev) => ({ ...prev, liteMode: true, proMode: false }))
    // Pequeno delay para garantir que settingsRef seja atualizado antes do envio
    setTimeout(() => {
      sendMessageRef.current?.(userMessage, terminalContext, {
        uiContent: `${userMessage} *(Agente Lite)*`,
      })
    }, 50)
  }, [])

  // ─── runCommand ───────────────────────────────────────────────────────────
  const runCommand = useCallback(
    async (command) => {
      if (!executeCommand) return

      setMessages((prev) => [
        ...prev,
        { role: 'system', content: `Executando: \`${command}\``, timestamp: Date.now() },
      ])

      try {
        const output = await executeWithSuCheck(command)
        const outputContext = `$ ${command}\n${output || '(sem output)'}`

        await sendMessage(
          'Analise o output do terminal (comando que acabou de ser executado) e explique o resultado, com próximos passos se fizer sentido.',
          outputContext,
          { uiContent: 'Analisando a saída mostrada no terminal…' }
        )
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'error',
            content: `Erro ao executar \`${command}\`: ${err?.message || 'falha'}`,
            timestamp: Date.now(),
          },
        ])
      }
    },
    [executeCommand, executeWithSuCheck, sendMessage]
  )

  const sendSelection = useCallback(
    (selectedText) => {
      if (!selectedText?.trim()) return
      setIsOpen(true)
      sendMessage(
        'Explique o que está acontecendo neste trecho do terminal.',
        selectedText,
        { uiContent: 'Trecho selecionado enviado para análise.' }
      )
    },
    [sendMessage]
  )

  return {
    isOpen,
    open,
    close,
    toggle,
    messages,
    loading,
    error,
    settings,
    setSettings,
    tokenStats,
    sendMessage,
    runCommand,
    sendSelection,
    newConversation,
    retryAsLite,
    // Su escalation
    suModal,
    confirmSu,
    cancelSu,
    isLinuxProfile: isLinuxProfileSlug(systemProfileSlug),
    // Execution plan (planner)
    executionPlan,
    approveExecutionPlan,
    rejectExecutionPlan,
  }
}
